/*
  JWC header menu — sections/header.liquid (.jwc-nav)

  1. Slider. The menu is one row that never wraps. When it doesn't fit, an
     arrow appears at each end that has more to see; a click brings the next
     partly hidden item fully into view. Trackpad/touch swipes and Tab scroll
     the row natively.

  2. Hover. The theme's <header-dropdown> (global.js) opens a panel on hover
     and closes it 100 ms after the pointer leaves the menu item, which is
     not enough time to travel down to the panel. Its mouse handlers are
     stopped here and replaced with:
       - a short rest on an item before its panel opens, so sweeping across
         the menu doesn't flash panels;
       - the panel stays open while the pointer is anywhere in the header or
         the panel. It switches when another item is hovered, and waits
         longer while the pointer is heading down into the open panel;
       - leaving the header (or moving onto the logo or icons) closes it
         after a moment, with a quick fade.
     Its keyboard handling (Enter / Space on an item) is unchanged.

  3. Placement. Panels hang from the bottom of the header; a single-column
     panel (e.g. Wall Of Fame) is lined up under its item wherever the row is
     scrolled.

  4. Touch: a tap opens or closes a menu, and a tap anywhere else closes it.
     Keyboard: Escape closes the open menu, and tabbing out of it closes it.
     Theme editor: a menu the editor pins open stays open.
*/
(() => {
  if (window.jwcHeaderNav) return;

  const OPEN_DELAY = 80; // rest on an item before its panel opens
  const SWITCH_DELAY = 70; // from an open panel onto another item
  const AIM_DELAY = 320; // …while the pointer is heading down into the open panel
  const CLOSE_DELAY = 220; // after leaving the header, or onto the logo / icons
  const FADE_OUT = 180; // jwc-dd-out in header.liquid

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const navs = new Set();

  class JwcHeaderNav {
    constructor(header, root) {
      this.header = header;
      this.root = root;
      this.row = root.querySelector('.header-shortlinks');
      this.section = header.closest('.shopify-section') || header.parentElement;
      this.timer = 0;
      this.closer = 0;
      this.hovered = null;
      this.trail = [];
      this.pointer = 'mouse';

      // Slider
      root.querySelector('[data-jwc-nav-prev]')?.addEventListener('click', () => this.page(-1));
      root.querySelector('[data-jwc-nav-next]')?.addEventListener('click', () => this.page(1));
      this.row.addEventListener('scroll', () => this.refresh(), { passive: true });
      this.row.addEventListener('focusin', (event) => this.keepInView(event.target));
      new ResizeObserver(() => this.refresh()).observe(this.row);
      document.fonts?.ready.then(() => this.refresh());
      this.revealCurrent();
      this.refresh();

      // Hover. The theme's own mouseenter/mouseleave handlers on the items
      // never see these events: they are stopped on their way down.
      const swallow = (event) => {
        if (event.target !== this.row && event.target.closest?.('header-dropdown')) event.stopPropagation();
      };
      this.row.addEventListener('mouseenter', swallow, true);
      this.row.addEventListener('mouseleave', swallow, true);
      header.addEventListener('pointerover', (event) => this.over(event));
      header.addEventListener('pointerleave', (event) => this.leave(event));
      header.addEventListener('pointermove', (event) => this.track(event), { passive: true });

      // Clicks, keys, and menus opening or closing by any route.
      this.row.addEventListener('click', (event) => this.click(event));
      this.row.addEventListener('keydown', (event) => this.keydown(event));
      this.row.addEventListener('focusout', (event) => this.focusout(event));
      this.row.addEventListener('toggle', (event) => this.toggled(event), true);
    }

    get menus() {
      return [...this.row.querySelectorAll('details.header-dropdown')];
    }

    pinned(details) {
      return details.hasAttribute('data-theme-editor-pin');
    }

    /* ---- Slider ----------------------------------------------------------- */

    // Runs on scroll and resize (both already once per frame at most).
    refresh() {
      const end = this.row.scrollWidth - this.row.clientWidth;
      const x = this.row.scrollLeft;
      this.root.classList.toggle('can-scroll-prev', x > 1);
      this.root.classList.toggle('can-scroll-next', x < end - 1);
      this.placePanels();
    }

    // Like turning a page: the next partly hidden item comes fully into view,
    // with as much after it as fits.
    page(direction) {
      this.closeAll();
      const view = this.row.getBoundingClientRect();
      const inset = this.root.querySelector('.jwc-nav__arrow')?.offsetWidth || 40;
      const items = [...this.row.children].filter((item) => item.getBoundingClientRect().width > 0);
      let delta = 0;
      if (direction > 0) {
        const item = items.find((el) => el.getBoundingClientRect().right > view.right - inset + 1);
        if (item) delta = item.getBoundingClientRect().left - (view.left + inset);
      } else {
        const item = items.reverse().find((el) => el.getBoundingClientRect().left < view.left + inset - 1);
        if (item) delta = item.getBoundingClientRect().right - (view.right - inset);
      }
      if (Math.abs(delta) < 24) delta = direction * view.width * 0.6;
      this.row.scrollBy({ left: delta, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    // Keyboard: the browser scrolls a focused item into the row, but may
    // leave it under an arrow; this moves it clear. (Links inside an open
    // panel don't move the row.)
    keepInView(target) {
      if (target.closest('.sub-menu-content')) return;
      const item = [...this.row.children].find((el) => el.contains(target));
      if (!item) return;
      const view = this.row.getBoundingClientRect();
      const box = item.getBoundingClientRect();
      const inset = this.root.querySelector('.jwc-nav__arrow')?.offsetWidth || 0;
      let delta = 0;
      if (box.left < view.left + inset) delta = box.left - (view.left + inset);
      else if (box.right > view.right - inset) delta = box.right - (view.right - inset);
      if (delta) this.row.scrollBy({ left: delta, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    // On a page the menu links to, its item starts in view.
    revealCurrent() {
      const here = location.pathname.replace(/\/$/, '');
      if (!here) return;
      const link = [...this.row.querySelectorAll('a[href]')].find(
        (a) => !a.getAttribute('href').startsWith('#') && a.pathname.replace(/\/$/, '') === here
      );
      const item = link && [...this.row.children].find((el) => el.contains(link));
      if (!item) return;
      const view = this.row.getBoundingClientRect();
      const box = item.getBoundingClientRect();
      if (box.left >= view.left && box.right <= view.right) return;
      this.row.scrollLeft += box.left + box.width / 2 - (view.left + view.width / 2);
    }

    /* ---- Panels ----------------------------------------------------------- */

    // A single-column panel lines its links up with its item's label, kept
    // inside the screen. (Full-width panels need no placing.)
    placePanels() {
      const box = this.section.getBoundingClientRect();
      for (const details of this.menus) {
        const panel = details.querySelector(':scope > .sub-menu-content.--single');
        const summary = details.querySelector(':scope > summary');
        if (!panel || !summary) continue;
        const item = summary.getBoundingClientRect();
        if (!item.width) continue;
        const panelStyle = getComputedStyle(panel);
        // Closed panels have no width yet: use the last one seen, or the widest.
        if (panel.offsetWidth) panel.jwcWidth = panel.offsetWidth;
        const width = panel.jwcWidth || parseFloat(panelStyle.maxWidth) || parseFloat(panelStyle.width) || 0;
        const indent = (parseFloat(getComputedStyle(summary).paddingLeft) || 0) - (parseFloat(panelStyle.paddingLeft) || 0);
        const left = Math.max(12, Math.min(item.left - box.left + indent, box.width - width - 12));
        panel.style.setProperty('--jwc-dd-left', `${Math.round(left)}px`);
      }
    }

    open(details) {
      clearTimeout(this.timer);
      clearTimeout(this.closer);
      for (const other of this.menus) if (other !== details) this.close(other);
      if (details.hasAttribute('data-jwc-closing')) {
        clearTimeout(details.jwcFade);
        details.removeAttribute('data-jwc-closing');
      }
      if (!details.open) {
        // Opened first, then placed: both happen before the next paint, and
        // the panel's real width is known.
        details.open = true;
        this.placePanels();
      }
      this.sync();
    }

    close(details, now = false) {
      if (!details.open || this.pinned(details) || details.hasAttribute('data-jwc-closing')) return;
      if (now || reducedMotion.matches) {
        details.open = false;
        this.sync();
        return;
      }
      details.setAttribute('data-jwc-closing', '');
      details.jwcFade = setTimeout(() => {
        details.removeAttribute('data-jwc-closing');
        details.open = false;
        this.sync();
      }, FADE_OUT);
    }

    closeAll(now = false) {
      clearTimeout(this.timer);
      clearTimeout(this.closer);
      for (const details of this.menus) this.close(details, now);
    }

    // The theme's "a menu is open" state on the header (solid background).
    sync() {
      this.header.classList.toggle('sub-menu-open', this.menus.some((details) => details.open));
    }

    /* ---- Hover ------------------------------------------------------------ */

    track(event) {
      if (event.pointerType !== 'mouse') return;
      this.trail.push([event.clientY, event.timeStamp]);
      while (this.trail.length > 2 && event.timeStamp - this.trail[0][1] > 120) this.trail.shift();
    }

    headingDown() {
      const first = this.trail[0];
      const last = this.trail[this.trail.length - 1];
      return Boolean(first && last && last[0] - first[0] > 4);
    }

    // What the pointer is over: a menu (its item or its open panel), a plain
    // menu link, the logo / quick links / icons ('away'), or empty header
    // space ('neutral').
    region(target) {
      const details = target.closest('details.header-dropdown');
      if (details && this.row.contains(details)) return details;
      const item = target.closest('.header-shortlinks > *');
      if (item && this.row.contains(item)) return item;
      if (target.closest('.header-logo, .header-item-link, .jwc-top-links')) return 'away';
      return 'neutral';
    }

    over(event) {
      if (event.pointerType !== 'mouse') return;
      const region = this.region(event.target);
      if (region === this.hovered) return;
      this.hovered = region;
      clearTimeout(this.closer);
      clearTimeout(this.timer);
      const current = this.menus.find((details) => details.open && !details.hasAttribute('data-jwc-closing'));

      if (region instanceof HTMLDetailsElement) {
        if (region === current) return;
        const delay = !current ? OPEN_DELAY : this.headingDown() ? AIM_DELAY : SWITCH_DELAY;
        this.timer = setTimeout(() => this.open(region), delay);
      } else if (region !== 'neutral' && current) {
        // Another menu link, the logo or an icon: the panel gives way.
        const delay = region === 'away' ? CLOSE_DELAY : this.headingDown() ? AIM_DELAY : SWITCH_DELAY;
        this.timer = setTimeout(() => this.closeAll(), delay);
      }
      // Empty header space (e.g. on the way down to the panel): no change.
    }

    leave(event) {
      if (event.pointerType !== 'mouse') return;
      this.hovered = null;
      clearTimeout(this.timer);
      if (this.menus.some((details) => details.open)) {
        this.closer = setTimeout(() => this.closeAll(), CLOSE_DELAY);
      }
    }

    /* ---- Click, touch, keyboard ------------------------------------------- */

    pointerdown(event) {
      this.pointer = event.pointerType;
      if (!this.header.contains(event.target)) this.closeAll();
    }

    click(event) {
      const summary = event.target.closest('summary');
      if (!summary || !this.row.contains(summary)) return;
      // A mouse click on an item opens it (hover may already have): it never
      // toggles the panel shut under the pointer. Taps, and clicks from the
      // keyboard or a screen reader (detail 0), toggle as usual.
      if (this.pointer !== 'mouse' || event.detail === 0) return;
      event.preventDefault();
      this.open(summary.parentElement);
    }

    toggled(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('header-dropdown')) return;
      if (details.open) {
        for (const other of this.menus) if (other !== details) this.close(other, true);
        this.placePanels();
      }
      this.sync();
    }

    keydown(event) {
      if (event.key !== 'Escape') return;
      const details = event.target.closest('details.header-dropdown[open]');
      if (!details || this.pinned(details)) return;
      event.preventDefault();
      this.close(details, true);
      details.querySelector(':scope > summary')?.focus();
    }

    focusout(event) {
      const details = event.target.closest('details.header-dropdown[open]');
      const next = event.relatedTarget;
      if (details && next && !details.contains(next)) this.close(details, true);
    }
  }

  function init(scope) {
    for (const nav of navs) if (!nav.header.isConnected) navs.delete(nav);
    const header = scope?.querySelector?.('#MainHeader') || document.getElementById('MainHeader');
    const root = header?.querySelector('[data-jwc-nav]');
    if (!root || root.jwcNav || !root.querySelector('.header-shortlinks')) return;
    root.jwcNav = new JwcHeaderNav(header, root);
    navs.add(root.jwcNav);
  }

  document.addEventListener(
    'pointerdown',
    (event) => {
      for (const nav of navs) nav.pointerdown(event);
    },
    true
  );

  // The sticky header slid away, or the window became the mobile layout.
  new MutationObserver(() => {
    if (!document.documentElement.classList.contains('sticky-header-hide')) return;
    for (const nav of navs) nav.closeAll(true);
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  window.matchMedia('(max-width: 767px)').addEventListener('change', (event) => {
    if (event.matches) for (const nav of navs) nav.closeAll(true);
  });

  // Theme editor: the header section re-renders as settings change.
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.jwcHeaderNav = { init };
  init(document);
})();
